import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Typography,
  Box,
} from '@mui/material';
import { WarningAmber } from '@mui/icons-material';
import { Checkbox, FormControlLabel } from '@mui/material';
import PropTypes from 'prop-types';

const ConsentDialog = ({
  open,
  onClose,
  onConfirm,
  maxTries,
  remainingTries,
  isSimulator = false,
  isUnlimited = false,
}) => {
  const [agreed1, setAgreed1] = React.useState(false);
  const [agreed2, setAgreed2] = React.useState(false);

  // Reset agreement when dialog opens
  React.useEffect(() => {
    if (open) {
      setAgreed1(false);
      setAgreed2(false);
    }
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby='alert-dialog-title'
      aria-describedby='alert-dialog-description'
    >
      <DialogTitle
        id='alert-dialog-title'
        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
      >
        <WarningAmber color='warning' />
        {isSimulator ? 'Iniciando Simulador' : 'Atenção: Atividade Avaliativa'}
      </DialogTitle>
      <DialogContent>
        {isSimulator ? (
          <>
            <DialogContentText id='alert-dialog-description'>
              Você está prestes a iniciar sua prática no simulador imersivo.<br />
              Recomendamos uma conexão estável com a internet.
            </DialogContentText>
            <Box
              sx={{
                my: 2,
                p: 2,
                bgcolor: '#fff7ed',
                borderRadius: 1,
                border: '1px solid #fdba74',
              }}
            >
              <Typography variant='subtitle2' color='warning.dark' gutterBottom>
                Informações da Atividade:
              </Typography>
              <Typography variant='body2' component='div' sx={{ mb: 0.5 }}>
                <strong>Tempo por sessão:</strong> até 30 minutos
              </Typography>
              <Typography variant='body2' component='div'>
                <strong>Acessos permitidos:</strong> ilimitados
              </Typography>
              <Typography
                variant='body2'
                color='text.secondary'
                sx={{ mt: 2, fontStyle: 'italic' }}
              >
                Aviso: Caso haja muitos acessos simultâneos, você entrará em uma fila de espera. Se sair da fila antes do início, sua tentativa não será contabilizada.
              </Typography>
            </Box>
            <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={agreed1}
                    onChange={e => setAgreed1(e.target.checked)}
                    color='primary'
                  />
                }
                label={
                  <Typography variant='body2'>
                    Li e estou ciente das condições de acesso e funcionamento do simulador.
                  </Typography>
                }
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={agreed2}
                    onChange={e => setAgreed2(e.target.checked)}
                    color='primary'
                  />
                }
                label={
                  <Typography variant='body2'>
                    Li e concordo com o{' '}
                    <a href='/termos-de-confidencialidade' target='_blank' rel='noopener noreferrer' style={{ color: '#1976d2', textDecoration: 'none', fontStyle: 'italic' }}>
                      Anexo de Confidencialidade do Simulador
                    </a>
                    .
                  </Typography>
                }
              />
            </Box>
          </>
        ) : (
          <>
            <DialogContentText id='alert-dialog-description'>
              Esta atividade possui um limite de tentativas para aprovação.
            </DialogContentText>
            <Box
              sx={{
                my: 2,
                p: 2,
                bgcolor: '#fff7ed',
                borderRadius: 1,
                border: '1px solid #fdba74',
              }}
            >
              <Typography variant='subtitle2' color='warning.dark' gutterBottom>
                Status das Tentativas:
              </Typography>
              <Typography variant='body2' component='div'>
                <strong>Máximo permitido:</strong>{' '}
                {isUnlimited ? 'Ilimitado' : `${maxTries || 3} tentativas`}
              </Typography>
              {!isUnlimited && (
                <Typography variant='body2' component='div'>
                  <strong>Restantes:</strong>{' '}
                  {`${remainingTries !== undefined ? remainingTries : maxTries || 3} tentativas`}
                </Typography>
              )}
              <Typography
                variant='body2'
                color='text.secondary'
                sx={{ mt: 1, fontStyle: 'italic' }}
              >
                Aviso: Se houver muitos acessos, você entrará em uma fila. Caso
                decida sair da fila, sua tentativa não será contada.
              </Typography>
            </Box>
            {!isUnlimited && (
              <DialogContentText
                color='error.main'
                sx={{ fontSize: '0.9rem', fontWeight: 500 }}
              >
                Importante: Se você reprovar {maxTries || 3} vezes, o progresso
                deste módulo será resetado e você precisará refazer todas as aulas.
              </DialogContentText>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} color='inherit'>
          Cancelar
        </Button>
        <Button
          onClick={onConfirm}
          variant='contained'
          color='primary'
          autoFocus
          disabled={isSimulator && (!agreed1 || !agreed2)}
        >
          {isSimulator ? 'Iniciar Simulação' : 'Estou Ciente e Quero Iniciar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

ConsentDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  maxTries: PropTypes.number,
  remainingTries: PropTypes.number,
  isSimulator: PropTypes.bool,
  isUnlimited: PropTypes.bool,
};

export default ConsentDialog;
